import { Dispatch, useEffect } from 'react';
import { useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import {
  KNO_ORGANIZATION_BLOCK_ID,
  PROSECUTOR_ORGANIZATION_BLOCK_ID,
  SUPERVISION_TYPE_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { PM_TYPE_BLOCK_ID } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { IFederalLawErvk } from 'APIPackage/CatalogsModule/interfaces/IFederalLawErvk';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { createTypedUseFormValue } from 'hooks/useTypedFormField';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { ProfKindCodesEnum } from 'APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { dictionariesFilteredFederalLawsGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { ICreatePreventionForm } from 'PreventionPackage/MainModule/interfaces/ICreatePreventionForm';

import styles from './CreatePreventionModal.module.scss';
import { useCreatePreventionModal } from './hooks/useCreatePreventionModal';

interface IFormFieldsProps {
  knoOrganizationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  federalLawsSelectOptions: IDictionarySelectOption<IFederalLawErvk>[] | undefined;
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[] | undefined;
  userKnoOrganisations: IUserOrganisation[];
}

const useFieldValue = createTypedUseFormValue<ICreatePreventionForm>();

export const FormFields: React.FC<IFormFieldsProps> = ({
  userProsecutorOrganisationsSelectOptions,
  knoOrganizationsSelectOptions,
  federalLawsSelectOptions,
  userKnoOrganisations,
}) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const { supervisionSelectOptions, profKindSelectOptions, reasonTypesSelectOptions } = useCreatePreventionModal();

  const startDate = useFieldValue('startDate');
  const parsedStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

  const profKindValue = useFieldValue('profKind');
  const isTypeOP = profKindValue?.code === ProfKindCodesEnum.OP;

  const form = useForm<ICreatePreventionForm>();

  const startDateRaw = useFieldValue('startDate');
  const stopDateRaw = useFieldValue('stopDate');

  const startDateLabel = isTypeOP ? 'Дата объявления предостережения' : 'Дата начала мероприятия';

  /**
   * useEffect для поиска и выбора предзаполненного значения для "Контрольный (надзорный) орган"
   */
  useEffect(() => {
    if (userKnoOrganisations && knoOrganizationsSelectOptions) {
      const byDefaultKno = userKnoOrganisations.find(i => i.byDefault);

      if (byDefaultKno) {
        const knoOrg = knoOrganizationsSelectOptions.find(i => i.id?.toString() === byDefaultKno.id);

        if (knoOrg) {
          form?.change('knoOrganizationPm', knoOrg);
          knoOrg?.value?.dictVersionId &&
            dispatch(dictionariesFilteredFederalLawsGet(knoOrg.value.dictVersionId, true));
        }
      }
    }
  }, [form, userKnoOrganisations, knoOrganizationsSelectOptions]);

  useEffect(() => {
    if (userProsecutorOrganisationsSelectOptions) {
      const byDefault = userProsecutorOrganisationsSelectOptions.find(i => i.byDefault);

      if (byDefault) {
        form?.change('prosecutorOrganization', byDefault);
      }
    }
  }, [form, userKnoOrganisations, knoOrganizationsSelectOptions, userProsecutorOrganisationsSelectOptions]);

  return (
    <>
      <Row notWrap>
        <FieldBlock
          label="Контрольный (надзорный) орган"
          placementLabel="left"
          id={KNO_ORGANIZATION_BLOCK_ID}
          className={styles.FieldWithOutTooltip}
        >
          <SelectInputField
            id="knoOrganizationPm"
            name="knoOrganizationPm"
            options={knoOrganizationsSelectOptions}
            placeholder="Выберите значение"
            className={styles.FullWidth}
            isSearchable
            validate={makeRequired('Поле не может быть пустым')}
            disabled={true}
          />
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          className={styles.FieldWithOutTooltip}
          placementLabel="left"
          label="Наименование прокуратуры"
          id={PROSECUTOR_ORGANIZATION_BLOCK_ID}
        >
          <SelectInputField
            id="prosecutorOrganization"
            name="prosecutorOrganization"
            placeholder="Выберите значение"
            options={userProsecutorOrganisationsSelectOptions}
            className={styles.FullWidth}
            validate={makeRequired('Поле не может быть пустым')}
            disabled={true}
          />
        </FieldBlock>
      </Row>
      <Row notWrap>
        <FieldBlock
          label={
            <>
              Федеральный закон, в <br /> соответствии с которым <br /> проводится мероприятие
              <span className={styles.redStar}>*</span>
            </>
          }
          placementLabel="left"
          id={KNO_ORGANIZATION_BLOCK_ID}
        >
          <div className={styles.InputCenter}>
            <SelectInputField
              id="federalLaw"
              name="federalLaw"
              options={federalLawsSelectOptions}
              placeholder="Выберите значение"
              disabled={false}
              className={styles.FullWidth}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </div>
        </FieldBlock>
        <div className={styles.TooltipWrapper}>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующем поле:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </div>
      </Row>
      <Row notWrap>
        <FieldBlock
          label={
            <>
              Вид государственного контроля (надзора)
              <span className={styles.redStar}>*</span>
            </>
          }
          placementLabel="left"
          id={SUPERVISION_TYPE_BLOCK_ID}
        >
          <div className={styles.InputCenter}>
            <SelectInputField
              id="supervisionTypePm"
              name="supervisionTypePm"
              options={supervisionSelectOptions}
              placeholder="Выберите значение"
              isClearable
              isSearchable
              disabled={false}
              className={styles.FullWidth}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </div>
        </FieldBlock>
        <div className={styles.TooltipWrapper}>
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующих полях:
                <ul>
                  <p>Федеральный закон, в соответствии с которым проводится мероприятие</p>
                  <p>Контрольный (надзорный) орган</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </div>
      </Row>
      <Row notWrap>
        <FieldBlock
          label={
            <>
              Вид мероприятия
              <span className={styles.redStar}>*</span>
            </>
          }
          placementLabel="left"
          id={PM_TYPE_BLOCK_ID}
        >
          <SelectInputField
            id="profKind"
            name="profKind"
            options={profKindSelectOptions}
            placeholder="Выберите значение"
            isClearable
            isSearchable
            disabled={false}
            className={styles.FullWidth}
            validate={makeRequired('Поле не может быть пустым')}
          />
        </FieldBlock>
        <Tooltip
          placement="right"
          type="info"
          content={
            <div className="shared-tooltip-info-content">
              Поле зависит от значения в следующих полях:
              <ul>
                <p>Контрольный (надзорный) орган</p>
                <p>Вид контроля (надзора)</p>
              </ul>
            </div>
          }
        >
          <span className="shared-tooltip-info-icon" />
        </Tooltip>
      </Row>
      {!isTypeOP ? (
        <Row>
          <div className={styles.dateRangeContainer}>
            <div className={styles.dateFieldWrapper}>
              <label htmlFor="startDate" className={styles.dateLabel}>
                {startDateLabel}
                <span className={styles.redStar}>*</span>
              </label>
              <DatePickerField className={styles.datePicker} name={`startDate`} minDate={new Date()} opensOnTop />
            </div>
            <div className={styles.dateFieldWrapper}>
              <label htmlFor="stopDate" className={styles.dateLabel}>
                Дата окончания мероприятия
                <span className={styles.redStar}>*</span>
              </label>
              <DatePickerField className={styles.datePicker} name={`stopDate`} minDate={parsedStartDate} opensOnTop />
            </div>
          </div>
        </Row>
      ) : (
        <div className={styles.input_container}>
          <div className={styles.label}>
            <label htmlFor="startDate" className={styles.dateLabel}>
              {startDateLabel}
              <span className={styles.redStar}>*</span>
            </label>
          </div>
          <DatePickerField className={styles.datePicker} name={`startDate`} minDate={new Date()} opensOnTop />
        </div>
      )}
      <FieldBlock
        label={
          <>
            Основание проведения мероприятия
            <span className={styles.redStar}>*</span>
          </>
        }
        id={PM_TYPE_BLOCK_ID}
      >
        <Row notWrap>
          <SelectInputField
            id="reason.type"
            name="reason.type"
            options={(isTypeOP ? !!startDateRaw : !!startDateRaw && !!stopDateRaw) ? reasonTypesSelectOptions : []}
            placeholder="Выберите значение"
            isClearable
            isSearchable
            disabled={false}
            className={styles.FullWidth}
            validate={makeRequired('Поле не может быть пустым')}
          />
          <Tooltip
            placement="right"
            type="info"
            content={
              <div className="shared-tooltip-info-content">
                Поле зависит от значения в следующих полях:
                <ul>
                  <p>Контрольный (надзорный) орган</p>
                  <p>Вид контроля (надзора)</p>
                  <p>{isTypeOP ? 'Дата объявления предостережения' : 'Даты проведения мероприятия'}</p>
                </ul>
              </div>
            }
          >
            <span className="shared-tooltip-info-icon" />
          </Tooltip>
        </Row>
      </FieldBlock>
    </>
  );
};
